/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { updateUser, UserAPI } from "../../../../api/User";
import { PlayButtonAtom } from "../../../../atoms/PlayButtonAtom";
import { InternalUserAtom, UserAtom } from "../../../../atoms/UserAtom";
import { IUserIRoute } from "../../../../generated/Api";
import { useDefaultAlias } from "../../../../hook/useDefaultAlias";
import { SpaceBetween } from "../../../../styles/general";
import {
  HelpVideoNameMap,
  IHelpContexts,
} from "../../../../types/HelpVideosTypes";
import StyledButton from "../../../StyledButton";
import { useModal } from "../../Modal";
import AddressesSettings, {
  CurrentSelectedAliasIdAtom,
} from "./container/AddressesSettings";
import FooterSettings from "./container/FooterSettings";
import GeneralSettings from "./container/GeneralSettings";
import Tab from "./Tab";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid #eceeee;
  box-sizing: inherit;
  margin: 20px -22px 0px;
  width: 600px;
  overflow-x: hidden;

  height: 70vh;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 68px);
    margin: 0;
    padding: 0 0 96px;

    border-top: none;

    position: relative;
  }
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100% - 67px);

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 68px);
    min-height: 100%;
    margin: 0;
  }
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  grid-gap: 16px;

  @media screen and (max-width: 1024px) {
    background: white;
    border-top: 1px solid #cccccc;

    padding: 25px;

    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;

    justify-content: flex-end;
  }
`;

const GENERAL = 0;
const FOOTER = 1;

export default function EmailSettings(): JSX.Element {
  const [selected, setSelected] = useState(GENERAL);
  const [user, setUser] = useRecoilState(UserAtom);
  const [userInfoToUpdate, setUserInfoToUpdate] = useState<IUserIRoute>(
    user as IUserIRoute
  );
  const [internalUser, setInternalUser] = useRecoilState(InternalUserAtom);
  const modal = useModal();
  const setPlayButton = useSetRecoilState(PlayButtonAtom);

  const [isLoading, setIsLoading] = useState(false);
  const [temporarySendFromSelect, setTemporarySendFromSelect] = useState<
    string | null
  >(null);
  const setCurrentSelectedAlias = useSetRecoilState(CurrentSelectedAliasIdAtom);
  const resetCurrentSelected = useResetRecoilState(CurrentSelectedAliasIdAtom);

  const { defaultAlias } = useDefaultAlias({
    user,
    internalUser,
  });

  useEffect(() => {
    setCurrentSelectedAlias(defaultAlias?.id);
  }, [defaultAlias?.id]);

  async function onClickSave() {
    setIsLoading(true);
    if (
      (userInfoToUpdate.emailOptions.maxWidth !== undefined &&
        isNaN(userInfoToUpdate.emailOptions.maxWidth)) ||
      (userInfoToUpdate.emailOptions.maxWidth &&
        userInfoToUpdate.emailOptions.maxWidth < 400)
    ) {
      toast("Max width need to be more than 400px", {
        type: "warning",
      });
      setIsLoading(false);
      return;
    }

    if (!userInfoToUpdate.emailOptions.font.size) {
      toast("You must choose a font size", {
        type: "warning",
      });
      setIsLoading(false);
      return;
    }

    // To be cleaner, we set the selected alias as the first on saving!
    // If this is being used by an internalUser this is also going to be
    // propagated to the internalUser configuration
    let aliases = userInfoToUpdate.emailOptions.aliases;

    if (temporarySendFromSelect)
      aliases = [
        aliases.find((a) => a.id === temporarySendFromSelect), // Put it at the top
        ...aliases.filter((a) => a.id !== temporarySendFromSelect), // And filter from the rest
      ];

    const userInfoToUpdateCopy: IUserIRoute = {
      ...userInfoToUpdate,
      emailOptions: {
        ...userInfoToUpdate.emailOptions,
        aliases,
      },
    };

    const isSuccess = await updateUser(userInfoToUpdateCopy, {
      internalUserUid:
        temporarySendFromSelect && internalUser?.userUid
          ? internalUser.userUid
          : null,
    });

    if (internalUser) {
      // fetch the internal user as well
      const updatedInternalUser = await UserAPI.getInternalUser(
        internalUser?.userUid
      );

      setInternalUser(updatedInternalUser);
    }

    if (isSuccess) {
      setUser(userInfoToUpdateCopy as any);
      setCurrentSelectedAlias(temporarySendFromSelect);
      resetCurrentSelected();
      modal.popModal();
    }
    setIsLoading(false);
  }

  function onClickCancel() {
    modal.popModal();
  }

  function getContainer(): JSX.Element {
    if (selected === GENERAL) {
      setPlayButton({
        title: HelpVideoNameMap[IHelpContexts.GeneralSettings],
        contextTag: IHelpContexts.GeneralSettings,
      });
      return (
        <GeneralSettings
          user={userInfoToUpdate as any}
          setUser={(u: IUserIRoute) => setUserInfoToUpdate(u as any)}
        />
      );
    }
    if (selected === FOOTER) {
      setPlayButton({
        title: HelpVideoNameMap[IHelpContexts.FooterSettings],
        contextTag: IHelpContexts.FooterSettings,
      });
      return (
        <FooterSettings
          user={userInfoToUpdate as IUserIRoute}
          setUser={(u: IUserIRoute) => setUserInfoToUpdate(u as IUserIRoute)}
        />
      );
    }

    setPlayButton({
      title: HelpVideoNameMap[IHelpContexts.SendFrom],
      contextTag: IHelpContexts.SendFrom,
    });
    return (
      <AddressesSettings
        user={userInfoToUpdate as IUserIRoute}
        internalUser={internalUser}
        setUser={(u: IUserIRoute) => setUserInfoToUpdate(u as IUserIRoute)}
        temporarySelectionToParent={(value) => {
          setTemporarySendFromSelect(value);
        }}
      />
    );
  }

  return (
    <ModalBody>
      <Container>
        <Tab
          selected={selected}
          handleSelected={(screen) => setSelected(screen)}
        />
        {getContainer()}
      </Container>

      <SpaceBetween
        css={css`
          border-top: 1px solid #eceeee;
          padding: 24px 20px 0px;

          @media screen and (max-width: 1024px) {
            border-top: none;
            padding: 0;
          }
        `}
      >
        <div />

        <Buttons>
          <StyledButton
            inverted
            onClick={onClickCancel}
            style={{ width: "92px" }}
          >
            Cancel
          </StyledButton>

          <StyledButton
            isLoading={isLoading}
            style={{ width: "92px" }}
            onClick={onClickSave}
          >
            Save
          </StyledButton>
        </Buttons>
      </SpaceBetween>
    </ModalBody>
  );
}
